import { useEffect, useMemo, useState } from 'react'
import { useWorkspace } from '../data/workspace'
import { subscribeTrackerDays, subscribeTrackers, writeTrackerDay } from '../data/trackerStore'
import { trackerStats, type Tracker, type TrackerDay, type TrackerStats } from '../data/trackers'
import { Heatmap } from '../ui/Heatmap'
import { TrackersEditor } from '../ui/TrackersEditor'
import { Screen } from '../ui/Screen'
import { formatClock, formatDayLabel } from '../lib/format'
import { shiftDayKey, todayKey, type DayKey } from '../lib/time'

const RANGES = [
  { days: 90, label: '3 months' },
  { days: 182, label: '6 months' },
  { days: 364, label: 'A year' },
] as const

export function TrackPage() {
  const { uid, settings } = useWorkspace()
  const [trackers, setTrackers] = useState<Tracker[]>([])
  const [days, setDays] = useState<TrackerDay[]>([])
  const [span, setSpan] = useState<number>(182)
  const [showSettings, setShowSettings] = useState(false)
  const [selected, setSelected] = useState<{ trackerId: string; day: DayKey } | null>(null)

  const today = todayKey(settings.dayStartHour)
  // Back to the Monday, so the heatmap's first column is a whole week.
  const from = useMemo(() => {
    const start = shiftDayKey(today, -span)
    const weekday = (new Date(`${start}T00:00:00Z`).getUTCDay() + 6) % 7
    return shiftDayKey(start, -weekday)
  }, [today, span])

  useEffect(() => subscribeTrackers(uid, setTrackers), [uid])
  useEffect(() => subscribeTrackerDays(uid, from, today, setDays), [uid, from, today])

  /** Counts by day, per tracker — what both the heatmap and the figures read. */
  const byTracker = useMemo(() => {
    const map = new Map<string, Map<DayKey, TrackerDay>>()
    for (const day of days) {
      const existing = map.get(day.trackerId) ?? new Map<DayKey, TrackerDay>()
      existing.set(day.day, day)
      map.set(day.trackerId, existing)
    }
    return map
  }, [days])

  const live = trackers.filter((tracker) => !tracker.archived)
  const shown = showSettings ? trackers : live

  async function log(tracker: Tracker, day: DayKey, delta: number) {
    const entry = byTracker.get(tracker.id)?.get(day)
    const current = entry?.count ?? 0
    const next = tracker.kind === 'check' ? (current > 0 ? 0 : 1) : Math.max(0, current + delta)
    const times =
      next > current
        ? [...(entry?.times ?? []), Date.now()]
        : (entry?.times ?? []).slice(0, Math.max(0, next))
    await writeTrackerDay(uid, tracker.id, day, next, times)
  }

  return (
    <Screen wide scroll>
      <div className="flex flex-wrap items-center gap-2 pb-3">
        <h2 className="text-[14px] font-semibold">Trackers</h2>
        <div className="flex rounded-lg border border-line p-0.5">
          {RANGES.map((range) => (
            <button
              key={range.days}
              type="button"
              onClick={() => setSpan(range.days)}
              aria-pressed={span === range.days}
              className={`rounded-md px-2.5 py-1 text-[13px] font-medium transition-colors duration-150 ${
                span === range.days ? 'bg-raised text-text' : 'text-dim hover:text-text'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setShowSettings((current) => !current)}
          aria-expanded={showSettings}
          className="ml-auto rounded-md border border-line px-2.5 py-1 text-[13px] font-medium text-dim transition-colors duration-150 hover:border-line-strong hover:text-text"
        >
          {showSettings ? 'Done' : 'Set them up'}
        </button>
      </div>

      {showSettings ? (
        <div className="mb-3 overflow-hidden rounded-xl border border-line bg-surface">
          <TrackersEditor uid={uid} trackers={trackers} />
        </div>
      ) : null}

      {shown.length === 0 ? (
        <p className="rounded-xl border border-line bg-surface px-3 py-6 text-center text-[13px] leading-relaxed text-faint">
          Nothing tracked yet. Add one — cups of coffee, days you walked in, evenings you left
          early — and every day you log it shows up here.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {shown.map((tracker) => (
            <TrackerCard
              key={tracker.id}
              tracker={tracker}
              days={byTracker.get(tracker.id) ?? new Map()}
              from={from}
              today={today}
              selected={selected?.trackerId === tracker.id ? selected.day : null}
              onSelectDay={(day) =>
                setSelected((current) =>
                  current?.trackerId === tracker.id && current.day === day
                    ? null
                    : { trackerId: tracker.id, day },
                )
              }
              onLog={(day, delta) => void log(tracker, day, delta)}
            />
          ))}
        </ul>
      )}
    </Screen>
  )
}

interface CardProps {
  tracker: Tracker
  days: Map<DayKey, TrackerDay>
  from: DayKey
  today: DayKey
  selected: DayKey | null
  onSelectDay: (day: DayKey) => void
  onLog: (day: DayKey, delta: number) => void
}

function TrackerCard({ tracker, days, from, today, selected, onSelectDay, onLog }: CardProps) {
  const counts = useMemo(
    () => new Map([...days.entries()].map(([day, entry]) => [day, entry.count])),
    [days],
  )
  const stats = useMemo(() => trackerStats(counts, today), [counts, today])
  const todayCount = counts.get(today) ?? 0
  const day = selected ?? today
  const entry = days.get(day)

  return (
    <li className="overflow-hidden rounded-xl border border-line bg-surface">
      <div className="flex flex-wrap items-center gap-3 px-3 py-2.5">
        <span
          className="size-3 shrink-0 rounded-full"
          style={{ backgroundColor: tracker.color }}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-medium text-text">
            {tracker.label}
            {tracker.archived ? (
              <span className="pl-2 text-[11px] text-faint uppercase">retired</span>
            ) : null}
          </span>
          <span className="block text-[12px] text-faint">{summary(tracker, stats)}</span>
        </span>

        <div className="flex shrink-0 items-center gap-2">
          <DayControl
            tracker={tracker}
            count={todayCount}
            subject="today"
            yes="Yes, today"
            no="Mark today"
            onLog={(delta) => onLog(today, delta)}
          />
        </div>
      </div>

      <div className="border-t border-line px-3 py-3">
        <Heatmap
          from={from}
          to={today}
          counts={counts}
          color={tracker.color}
          unit={tracker.unit}
          selected={selected}
          onSelect={onSelectDay}
        />

        <div className="flex flex-wrap items-end gap-x-7 gap-y-2 pt-3">
          {tracker.kind === 'count' ? (
            <>
              <Figure value={String(stats.total)} label="in total" />
              <Figure value={String(stats.days)} label="days" />
              <Figure value={stats.perDay.toFixed(stats.perDay < 10 ? 1 : 0)} label="a day" />
            </>
          ) : (
            <>
              <Figure value={String(stats.days)} label="days" />
              <Figure
                value={stats.perDay > 0 ? `${Math.round(stats.perDay * 100)}%` : '—'}
                label="of days"
              />
            </>
          )}
          <Figure value={String(stats.streak)} label="day streak" />
          <Figure value={String(stats.longestStreak)} label="best run" />
          {tracker.kind === 'count' && stats.busiest ? (
            <Figure
              value={String(stats.busiest.count)}
              label={`most, ${formatDayLabel(stats.busiest.day, 'd MMM')}`}
            />
          ) : null}
        </div>

        {/* Any day is edited the way today is, with the same control. Picking a
            square used to only tell you what was there, which left marking a day
            other than today looking impossible. */}
        {selected ? (
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-line bg-raised px-3 py-2">
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-medium text-text">
                {formatDayLabel(day, 'EEEE d MMMM')}
              </span>
              <span className="block text-[12px] text-faint">{daySummary(tracker, entry)}</span>
            </span>
            <DayControl
              tracker={tracker}
              count={entry?.count ?? 0}
              subject={`on ${formatDayLabel(day, 'd MMMM')}`}
              yes="Yes"
              no="Mark it"
              onLog={(delta) => onLog(day, delta)}
            />
          </div>
        ) : null}
      </div>
    </li>
  )
}

/**
 * The control for one day. The header drives today with it and the panel under
 * the heatmap drives whichever day is picked, so marking last Tuesday is the
 * same gesture as marking today rather than a different, smaller one.
 */
function DayControl({
  tracker,
  count,
  subject,
  yes,
  no,
  onLog,
}: {
  tracker: Tracker
  count: number
  /** Named in the button labels a screen reader reads: "today", "on 8 September". */
  subject: string
  yes: string
  no: string
  onLog: (delta: number) => void
}) {
  if (tracker.kind === 'check') {
    return (
      <button
        type="button"
        onClick={() => onLog(1)}
        aria-pressed={count > 0}
        title={count > 0 ? `Unmark ${subject}` : `Mark ${subject}`}
        className={`shrink-0 rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ${
          count > 0
            ? 'border-transparent text-[#0a0b0c]'
            : 'border-line-strong text-dim hover:text-text'
        }`}
        style={count > 0 ? { backgroundColor: tracker.color } : undefined}
      >
        {count > 0 ? yes : no}
      </button>
    )
  }
  return (
    <div className="flex shrink-0 items-center gap-1">
      <StepButton label={`One fewer ${subject}`} onClick={() => onLog(-1)} disabled={count === 0}>
        −
      </StepButton>
      <span className="w-8 text-center text-[17px] leading-none font-semibold tnum">{count}</span>
      <StepButton label={`One more ${subject}`} onClick={() => onLog(1)}>
        +
      </StepButton>
    </div>
  )
}

/**
 * What one day reads as. A check has no amount and no meaningful time — pressing
 * the button at eleven at night says nothing about a day you worked from home —
 * so it says only that it happened.
 */
function daySummary(tracker: Tracker, entry: TrackerDay | undefined): string {
  const count = entry?.count ?? 0
  if (count === 0) return 'Nothing logged'
  if (tracker.kind === 'check') return 'Logged'
  const unit = tracker.unit ? ` ${tracker.unit}${count === 1 ? '' : 's'}` : ''
  const times =
    entry && entry.times.length > 0
      ? `, at ${entry.times.map((time) => formatClock(new Date(time))).join(', ')}`
      : ''
  return `${count}${unit}${times}`
}

/** What the header line says, which differs by kind: a check has no amount. */
function summary(tracker: Tracker, stats: TrackerStats): string {
  if (stats.days === 0) return 'Never logged'
  const days = `${stats.days} ${stats.days === 1 ? 'day' : 'days'}`
  if (tracker.kind === 'check') return `${days} so far`
  const unit = tracker.unit ? ` ${tracker.unit}${stats.total === 1 ? '' : 's'}` : ''
  return `${stats.total}${unit} over ${days}`
}

function StepButton({
  label,
  onClick,
  disabled = false,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="flex size-8 items-center justify-center rounded-lg border border-line text-[16px] text-dim transition-colors duration-150 hover:border-line-strong hover:text-text disabled:opacity-30"
    >
      {children}
    </button>
  )
}

function Figure({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <p className="text-[19px] leading-none font-semibold tracking-tight tnum">{value}</p>
      <p className="pt-1 text-[12px] leading-tight text-faint">{label}</p>
    </div>
  )
}
